import path from 'path'

import _ from 'lodash'
import { message } from 'antd'
import uuid from 'uuid'

import logger from '~/lib/logger'
import { CAD_EXTENSIONS } from '~/lib/cadExtensions'
import { AttachmentCreateType } from '~/__generated__/globalTypes'
import {
    CreateQmrRevisionDocument,
    CreateQuoteAttachmentRevisionDocument,
    CreateQuoteModelRevisionAttachmentDocument,
    MarkQuoteAttachmentRevisionUploadedDocument,
} from '~/gql/graphql'

export function isCadFile(file) {
    // match against some list of file extensions
    const fileExt = path.extname(file.name).toLowerCase()
    const isCAD = CAD_EXTENSIONS.some(cadExt => cadExt === fileExt)

    return isCAD
}

export function onProgress(e) {
    return { percent: e.percent }
}

export function createQuoteModelRevision({
    referenceId,
    size,
    contentType,
    fileName,
    units,
    revisionNotes,
    quoteModelId,
    quoteId,
    refetch,
    manualRefetch,
    isQueryRefactorEnabled,
}) {
    return (dispatch, getState, { client }) => {
        if (isQueryRefactorEnabled && manualRefetch) {
            return client
                .mutate({
                    mutation: CreateQmrRevisionDocument,
                    variables: {
                        uploadMetadata: {
                            referenceId,
                            contentType,
                            fileName,
                            fileSize: size,
                            units,
                        },
                        quoteModelId,
                        quoteId,
                        setAsSelectedForQuoteModel: true,
                        revisionNotes,
                    },
                })
                .then(manualRefetch(quoteId))
        } else {
            return client.mutate({
                mutation: CreateQmrRevisionDocument,
                variables: {
                    uploadMetadata: {
                        referenceId,
                        contentType,
                        fileName,
                        fileSize: size,
                        units,
                    },
                    quoteModelId,
                    quoteId,
                    setAsSelectedForQuoteModel: true,
                    revisionNotes,
                },
                refetchQueries: refetch,
            })
        }
    }
}

export async function uploadRevision({
    dispatch,
    file,
    quoteModelId,
    quoteId,
    units,
    revisionNotes,
    refetch,
    manualRefetch,
    isQueryRefactorEnabled,
}) {
    const referenceId = uuid.v4()

    const revisionUploadMetadata = await dispatch(
        createQuoteModelRevision({
            referenceId,
            size: file.size,
            contentType: file.type,
            fileName: file.name,
            units,
            revisionNotes,
            quoteModelId,
            quoteId,
            refetch,
            manualRefetch,
            isQueryRefactorEnabled,
        }),
    )
        .then(result => {
            const quoteModelRevision = result.data.createQuoteModelRevision
            const { uploadUrl, partRevision } = quoteModelRevision

            return {
                postUrl: uploadUrl,
                referenceId,
                quoteModelRevision,
                revisionId: partRevision.id,
            }
        })
        .catch(error => {
            logger.exception(error, {
                extra: { info: 'Failed to create quote model revision' },
            })
            message.error('Failed to upload revision')
            return {}
        })

    if (_.isEmpty(revisionUploadMetadata)) {
        logger.warn('Failed to create model or get uploadUrl', {
            file,
            revisionUploadMetadata,
        })
        return false
    }

    file.uploadUrl = revisionUploadMetadata.postUrl //eslint-disable-line require-atomic-updates
    file.uploadMetadata = revisionUploadMetadata //eslint-disable-line require-atomic-updates
    return file
}

export function createQuoteAttachmentRevision({
    file,
    quoteId,
    attachmentType,
    quoteAttachmentId,
    productAttachmentId,
}) {
    return (dispatch, getState, { client }) => {
        return client
            .mutate({
                mutation: CreateQuoteAttachmentRevisionDocument,
                variables: {
                    uploadMetadata: {
                        mimeType: file.type,
                        extension: path.extname(file.name),
                        fileName: file.name,
                        type: AttachmentCreateType.user_uploaded,
                    },
                    quoteId,
                    attachmentType,
                    quoteAttachmentId,
                    productAttachmentId,
                },
            })
            .then(result => result.data.createQuoteAttachmentRevision || {})
            .catch(error => {
                logger.exception(error, {
                    extra: { info: 'Failed to create quoteAttachmentRevision' },
                })

                message.error('Failed to upload attachment')
            })
    }
}

export function createQuoteModelRevisionAttachment({
    quoteModelRevisionId,
    quoteAttachmentRevisionId,
}) {
    return (dispatch, getState, { client }) => {
        return client.mutate({
            mutation: CreateQuoteModelRevisionAttachmentDocument,
            variables: {
                quoteModelRevisionId,
                quoteAttachmentRevisionId,
            },
        })
    }
}

export function markQuoteAttachmentRevisionUploaded({
    file,
    quoteId,
    refetchQuery,
    manualRefetch,
    isQueryRefactorEnabled,
}) {
    return (dispatch, getState, { client }) => {
        if (isQueryRefactorEnabled && manualRefetch) {
            return client
                .mutate({
                    mutation: MarkQuoteAttachmentRevisionUploadedDocument,
                    variables: {
                        id: file.quoteAttachmentRevisionId,
                    },
                })
                .then(manualRefetch(quoteId))
                .catch(error => {
                    logger.exception(error, {
                        extra: { info: 'Failed to mark attachment uploaded' },
                    })
                    message.error(`${file.name} failed to complete the upload`)
                })
        } else {
            return client
                .mutate({
                    mutation: MarkQuoteAttachmentRevisionUploadedDocument,
                    variables: {
                        id: file.quoteAttachmentRevisionId,
                    },
                    refetchQueries: refetchQuery,
                })
                .catch(error => {
                    logger.exception(error, {
                        extra: { info: 'Failed to mark attachment uploaded' },
                    })
                    message.error(`${file.name} failed to complete the upload`)
                })
        }
    }
}

export const genericUploadRevisionWarning =
    'The same part configuration (with the exception of advanced inspection) will be applied to the new revision but you may need to request a quote again if the price and/or DFM feedback cannot be updated automatically.'

export const imUploadRevisionWarning =
    'The same part configuration (with the exception of advanced inspection) will be applied to the new revision but you will need to request a quote again since the price and DFM feedback cannot be updated automatically for injection molding quotes.'
